import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Home, FileText, Layout, Users, BarChart3,
  Settings, ShoppingBag, ClipboardList, Database, Menu, X,
  Building2, Package,
} from 'lucide-react';
import { cn } from '@/lib/utils';

import { isPlatformAdmin, isCompanyStaff, isCompanyOwner, getEffectiveRole } from '@/lib/supabaseClient';

function getNavForRole() {
  const role = getEffectiveRole();
  const isAdmin = isPlatformAdmin();
  const isStaff = isCompanyStaff();
  const isOwner = isCompanyOwner();

  const nav = [];

  // Everyone sees the store
  nav.push({ section: 'Store', items: [
    { name: 'Shop', to: '/', icon: ShoppingBag },
  ]});

  // Platform admin sees admin tools
  if (isAdmin) {
    nav.push({ section: 'Platform Admin', items: [
      { name: 'Dashboard', to: '/home', icon: Home },
      { name: 'All Orders', to: '/orders', icon: ClipboardList },
      { name: 'Catalog', to: '/catalog', icon: Database },
      { name: 'Templates', to: '/templates', icon: Layout },
      { name: 'Quote Builder', to: '/quote-builder', icon: FileText },
      { name: 'All Contacts', to: '/all-contacts', icon: Users },
      { name: 'Reports', to: '/reports', icon: BarChart3 },
    ]});
  }

  // Company owner/member sees company tools
  if (!isAdmin && isStaff) {
    const companyItems = [
      { name: 'Dashboard', to: '/home', icon: Home },
      { name: 'Orders', to: '/orders', icon: ClipboardList },
      { name: 'Quote Builder', to: '/quote-builder', icon: FileText },
      { name: 'Contacts', to: '/contacts', icon: Users },
    ];

    if (isOwner) {
      companyItems.push({ name: 'Company', to: '/settings', icon: Building2 });
    }

    nav.push({ section: 'Company', items: companyItems });
  }

  // Customer sees only their stuff
  if (!isAdmin && !isStaff) {
    nav.push({ section: 'My Account', items: [
      { name: 'My Orders', to: '/my-orders', icon: Package },
    ]});
  }

  return nav;
}

export default function Sidebar() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [, setAuthTick] = useState(0);

  // Re-render when authContext is updated (e.g. after ProfileBadge fetches)
  useEffect(() => {
    const handler = () => setAuthTick(t => t + 1);
    window.addEventListener('authContextUpdated', handler);
    return () => window.removeEventListener('authContextUpdated', handler);
  }, []);

  const mainNav = getNavForRole();

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="p-4 border-b border-gray-200 cursor-pointer" onClick={() => { navigate('/'); setMobileOpen(false); }}>
        <div className="flex items-center gap-3 justify-center">
          <img src="/assets/images/agi-logo.png" alt="AGI" className="h-10 w-auto" />
        </div>
        <div className="flex flex-col items-center mt-2">
          <span className="text-base font-bold text-[#1E3A8A] tracking-tight">AGI Quote Builder</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 overflow-y-auto">
        {mainNav.map((group) => (
          <div key={group.section} className="mb-5">
            <h3 className="px-3 mb-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
              {group.section}
            </h3>
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.to}
                  end={item.to === '/'}
                  onClick={() => setMobileOpen(false)}
                  aria-label={item.name}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                      isActive
                        ? "bg-[#1E3A8A]/10 text-[#1E3A8A] font-semibold"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    )
                  }
                >
                  <item.icon className="h-4 w-4" aria-hidden="true" />
                  {item.name}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>


    </>
  );

  return (
    <>
      {/* Mobile hamburger button — hidden when drawer is open */}
      {!mobileOpen && (
        <button
          onClick={() => setMobileOpen(true)}
          className="fixed top-4 left-4 z-50 md:hidden bg-white border border-gray-200 rounded-lg p-2 shadow-sm"
          aria-label="Open navigation menu"
        >
          <Menu className="h-5 w-5 text-gray-700" />
        </button>
      )}

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile sidebar drawer */}
      <div className={cn(
        "fixed inset-y-0 left-0 z-50 bg-white w-64 flex flex-col transform transition-transform duration-200 md:hidden",
        mobileOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <button
          onClick={() => setMobileOpen(false)}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          aria-label="Close menu"
        >
          <X className="h-5 w-5" />
        </button>
        {sidebarContent}
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:flex bg-white border-r border-gray-200 w-64 flex-col flex-shrink-0 h-screen sticky top-0">
        {sidebarContent}
      </div>
    </>
  );
}
